import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, map, filter } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  AssignmentStatus,
  GenerationStatus,
  AssignmentResource,
  AssignmentQuestion,
} from './assignment.models';
import {
  PaginationMeta,
  ScoringGuideStandard,
  ScoringItemPreview,
} from '../../grading-chat/grading.types';

// ============================================
// SUBMISSION TYPES (for teacher dashboard)
// ============================================

export interface SubmissionListItem {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string | null;
  status: 'in_progress' | 'submitted' | 'graded' | 'expired';
  gradePercentage: number | null;
  totalPointsAwarded: number | null;
  totalPointsPossible: number | null;
  submittedAt: string | null;
  startedAt: string;
  createdAt: string;
}

export interface SubmissionAnswerDetail {
  questionId: string;
  selectedOptionId: string | null;
  textAnswer: string | null;
  maxPoints: number;
  pointsAwarded: number | null;
  feedback: string | null;
}

export interface SubmissionQuestionDetail {
  id: string;
  type: 'multiple_choice' | 'short_answer' | 'essay';
  questionText: string;
  options: { id: string; text: string; isCorrect: boolean }[];
  points: number;
  order: number;
}

export interface SubmissionDetail {
  id: string;
  assignmentId: string;
  studentId: string;
  studentName: string;
  studentEmail: string | null;
  status: 'in_progress' | 'submitted' | 'graded' | 'expired';
  gradePercentage: number | null;
  totalPointsAwarded: number | null;
  totalPointsPossible: number | null;
  submittedAt: string | null;
  startedAt: string;
  questionsCount: number;
  answeredCount: number;
  overallFeedback: string | null;
  strengths: string[];
  areasForImprovement: string[];
  answers: SubmissionAnswerDetail[];
  questions: SubmissionQuestionDetail[];
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface AssignmentResponse {
  id: string;
  teacherId: string | null;
  grade: string;
  subject: string;
  tier1SubjectId: string | null;
  tier2SubjectId: string | null;
  state: string | null;
  title: string;
  objective: string;
  resources: AssignmentResource[];
  questions: AssignmentQuestion[];
  dueDate?: string;
  timeLimit?: number;
  status: AssignmentStatus;
  shareCode?: string;
  scoringGuideId?: string | null;
  generationStatus: GenerationStatus;
  generationError: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present in list endpoint: number of students who submitted */
  submittedCount?: number;
  /** Present in list endpoint: total submissions (students who started) */
  totalSubmissionCount?: number;
}

export interface GenerationStatusResponse {
  generationStatus: GenerationStatus;
  generationError: string | null;
}

export interface UploadProgress {
  progress: number;
  completed: boolean;
}

// ============================================
// API SERVICE
// ============================================

@Injectable({
  providedIn: 'root',
})
export class AssignmentApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/assignments`;

  /**
   * Generate an example objective based on grade, subject, and state.
   * No assignment ID required.
   */
  generateExampleObjective(data: {
    grade: string;
    tier1SubjectId?: string;
    tier2SubjectId?: string;
    state?: string;
  }): Observable<{ objective: string }> {
    return this.http.post<{ objective: string }>(
      `${this.apiUrl}/generate-example-objective`,
      data,
    );
  }

  /**
   * Create a new assignment
   */
  createAssignment(data: {
    grade: string;
    tier1SubjectId?: string;
    tier2SubjectId?: string;
    state?: string;
    objective: string;
  }): Observable<AssignmentResponse> {
    return this.http.post<AssignmentResponse>(this.apiUrl, data);
  }

  /**
   * Get an assignment by ID
   */
  getAssignment(id: string): Observable<AssignmentResponse> {
    return this.http.get<AssignmentResponse>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update an assignment
   */
  updateAssignment(
    id: string,
    data: Partial<{
      grade: string;
      subject: string;
      tier1SubjectId: string;
      tier2SubjectId: string;
      state: string;
      title: string;
      objective: string;
      questionCount: number;
    }>,
  ): Observable<AssignmentResponse> {
    return this.http.put<AssignmentResponse>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Upload a resource file
   */
  uploadResource(
    assignmentId: string,
    file: File
  ): Observable<UploadProgress> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<AssignmentResponse>(
        `${this.apiUrl}/${assignmentId}/resources`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        map((event: HttpEvent<AssignmentResponse>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = event.total
                ? Math.round((100 * event.loaded) / event.total)
                : 0;
              return { progress, completed: false };
            case HttpEventType.Response:
              return { progress: 100, completed: true };
            default:
              return { progress: 0, completed: false };
          }
        })
      );
  }

  /**
   * Upload multiple resource files
   */
  uploadResources(
    assignmentId: string,
    files: File[]
  ): Observable<UploadProgress> {
    // Upload files sequentially and track overall progress
    let completedCount = 0;
    const totalFiles = files.length;

    return new Observable<UploadProgress>((subscriber) => {
      const uploadNext = (index: number) => {
        if (index >= files.length) {
          subscriber.next({ progress: 100, completed: true });
          subscriber.complete();
          return;
        }

        this.uploadResource(assignmentId, files[index]).subscribe({
          next: (progress) => {
            // Calculate overall progress
            const fileProgress = progress.progress / totalFiles;
            const overallProgress = Math.round(
              (completedCount / totalFiles) * 100 + fileProgress
            );
            subscriber.next({
              progress: overallProgress,
              completed: false,
            });

            if (progress.completed) {
              completedCount++;
              uploadNext(index + 1);
            }
          },
          error: (err) => subscriber.error(err),
        });
      };

      uploadNext(0);
    });
  }

  /**
   * Delete a resource file
   */
  deleteResource(
    assignmentId: string,
    resourceId: string
  ): Observable<AssignmentResponse> {
    return this.http.delete<AssignmentResponse>(
      `${this.apiUrl}/${assignmentId}/resources/${resourceId}`
    );
  }

  /**
   * Start question generation
   */
  startQuestionGeneration(assignmentId: string): Observable<void> {
    return this.http
      .post<{ message: string }>(
        `${this.apiUrl}/${assignmentId}/generate-questions`,
        {}
      )
      .pipe(map(() => undefined));
  }

  /**
   * Get generation status (for polling)
   */
  getGenerationStatus(
    assignmentId: string
  ): Observable<GenerationStatusResponse> {
    return this.http.get<GenerationStatusResponse>(
      `${this.apiUrl}/${assignmentId}/generation-status`
    );
  }

  /**
   * Update all questions (bulk — for reorder, delete, add)
   */
  updateQuestions(
    assignmentId: string,
    questions: AssignmentQuestion[]
  ): Observable<AssignmentResponse> {
    return this.http.put<AssignmentResponse>(
      `${this.apiUrl}/${assignmentId}/questions`,
      { questions }
    );
  }

  /**
   * Save a single question and regenerate its scoring guide item.
   * Returns the saved question, rubric, and scoring guide item ID.
   */
  saveQuestion(
    assignmentId: string,
    questionId: string,
    question: AssignmentQuestion,
  ): Observable<{
    question: AssignmentQuestion;
    rubric: ScoringItemPreview;
    scoringGuideItemId: string;
  }> {
    return this.http.put<{
      question: AssignmentQuestion;
      rubric: ScoringItemPreview;
      scoringGuideItemId: string;
    }>(
      `${this.apiUrl}/${assignmentId}/questions/${questionId}`,
      question,
    );
  }

  /**
   * Apply an AI action to a single question (no persist — local only)
   */
  applyQuestionAiAction(
    assignmentId: string,
    questionId: string,
    instruction: string,
  ): Observable<{ question: AssignmentQuestion }> {
    return this.http.post<{ question: AssignmentQuestion }>(
      `${this.apiUrl}/${assignmentId}/questions/${questionId}/ai-action`,
      { instruction },
    );
  }

  /**
   * Update settings (due date, time limit)
   */
  updateSettings(
    assignmentId: string,
    settings: { dueDate?: string | null; timeLimit?: number }
  ): Observable<AssignmentResponse> {
    return this.http.put<AssignmentResponse>(
      `${this.apiUrl}/${assignmentId}/settings`,
      settings
    );
  }

  /**
   * Publish assignment and get share code
   */
  publishAssignment(assignmentId: string): Observable<AssignmentResponse> {
    return this.http.post<AssignmentResponse>(
      `${this.apiUrl}/${assignmentId}/publish`,
      {}
    );
  }

  /**
   * Get paginated assignments for the current teacher
   */
  getAssignments(
    page = 1,
    limit = 10,
  ): Observable<{
    items: AssignmentResponse[];
    pagination: PaginationMeta;
  }> {
    return this.http.get<{
      items: AssignmentResponse[];
      pagination: PaginationMeta;
    }>(this.apiUrl, {
      params: { page: page.toString(), limit: limit.toString() },
    });
  }

  /**
   * Soft-delete a draft assignment.
   */
  deleteAssignment(assignmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${assignmentId}`);
  }

  /**
   * Assign an unowned assignment to the current user.
   * Used after signup/login to claim an assignment created during the wizard funnel.
   */
  assignToUser(assignmentId: string): Observable<AssignmentResponse> {
    return this.http.post<AssignmentResponse>(
      `${this.apiUrl}/${assignmentId}/assign`,
      {}
    );
  }

  /**
   * Get scoring guide items by their IDs (batch endpoint).
   * Also returns the relevant standards for display.
   */
  getScoringGuideItems(
    assignmentId: string,
    itemIds: string[],
  ): Observable<{
    items: ScoringItemPreview[];
    standards: ScoringGuideStandard[];
  }> {
    const ids = itemIds.join(',');
    return this.http.get<{
      items: ScoringItemPreview[];
      standards: ScoringGuideStandard[];
    }>(`${this.apiUrl}/${assignmentId}/scoring-guide-items?ids=${ids}`);
  }

  /**
   * Generate (or regenerate) a rubric for a specific question.
   * Returns the scoring item and its ID to be stored on the question.
   */
  generateQuestionRubric(
    assignmentId: string,
    questionId: string,
  ): Observable<{ rubric: ScoringItemPreview; scoringGuideItemId: string }> {
    return this.http.post<{
      rubric: ScoringItemPreview;
      scoringGuideItemId: string;
    }>(
      `${this.apiUrl}/${assignmentId}/questions/${questionId}/generate-rubric`,
      {},
    );
  }

  /**
   * Get all submissions for an assignment (with student info).
   * Teacher-only endpoint for the dashboard.
   */
  getAssignmentSubmissions(
    assignmentId: string,
  ): Observable<SubmissionListItem[]> {
    return this.http.get<SubmissionListItem[]>(
      `${this.apiUrl}/${assignmentId}/submissions`,
    );
  }

  /**
   * Get a single submission detail with questions and answers.
   * Teacher-only endpoint for the submission review page.
   */
  getSubmissionDetail(
    assignmentId: string,
    submissionId: string,
  ): Observable<SubmissionDetail> {
    return this.http.get<SubmissionDetail>(
      `${this.apiUrl}/${assignmentId}/submissions/${submissionId}`,
    );
  }
}
